import { createClient } from '@supabase/supabase-js';
import { captureFrontendException } from './sentry';

/**
 * Supabase Client Initialization
 * 
 * Reads Supabase URL and Anon Key from Vite's import.meta.env or Node's process.env.
 * VITE_SUPABASE_URL=your_supabase_url
 * VITE_SUPABASE_ANON_KEY=your_supabase_anon_key
 */

const getSupabaseConfig = () => {
  const metaEnv = typeof import.meta !== 'undefined' ? (import.meta as any)?.env : undefined;
  const procEnv = typeof process !== 'undefined' ? process.env : undefined;

  const url = 
    (metaEnv?.VITE_SUPABASE_URL) ||
    (procEnv?.VITE_SUPABASE_URL) ||
    (procEnv?.SUPABASE_URL) ||
    (metaEnv?.NEXT_PUBLIC_SUPABASE_URL) ||
    (procEnv?.NEXT_PUBLIC_SUPABASE_URL) ||
    '';

  const anonKey = 
    (metaEnv?.VITE_SUPABASE_ANON_KEY) ||
    (procEnv?.VITE_SUPABASE_ANON_KEY) ||
    (procEnv?.SUPABASE_ANON_KEY) ||
    (metaEnv?.NEXT_PUBLIC_SUPABASE_ANON_KEY) ||
    (procEnv?.NEXT_PUBLIC_SUPABASE_ANON_KEY) ||
    '';

  return { url, anonKey };
};

const { url: supabaseUrl, anonKey: supabaseAnonKey } = getSupabaseConfig();

function createDummyQueryBuilder(reason = 'Supabase not configured') {
  const dummyResult = Promise.resolve({ data: null, error: new Error(reason) });
  const builder: any = {
    select: () => builder,
    insert: () => dummyResult,
    upsert: () => dummyResult,
    update: () => builder,
    delete: () => builder,
    eq: () => builder,
    neq: () => builder,
    gt: () => builder,
    gte: () => builder,
    lt: () => builder,
    lte: () => builder,
    like: () => builder,
    ilike: () => builder,
    is: () => builder,
    in: () => builder,
    contains: () => builder,
    containedBy: () => builder,
    order: () => builder,
    limit: () => builder,
    range: () => builder,
    single: () => dummyResult,
    maybeSingle: () => dummyResult,
    then: (resolve: any, reject: any) => dummyResult.then(resolve, reject),
    catch: (reject: any) => dummyResult.catch(reject)
  };
  return builder;
}

function createDummyClient(reason = 'Supabase not configured') {
  return {
    auth: {
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      signInWithPassword: () => Promise.resolve({ data: null, error: new Error(reason) }),
      signOut: () => Promise.resolve({ error: null })
    },
    from: (_table: string) => createDummyQueryBuilder(reason),
    channel: () => ({ on: () => ({ subscribe: () => {} }), subscribe: () => {} }),
    removeChannel: () => {},
    rpc: () => Promise.resolve({ data: null, error: new Error(reason) })
  };
}

// Initialize Supabase client gracefully without crashing the app at load time
let supabaseInstance: any;
try {
  if (supabaseUrl && supabaseAnonKey) {
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false
      }
    });
  } else {
    console.warn('[Supabase] Environment variables missing. Using fallback mock client.');
    supabaseInstance = createDummyClient('Supabase environment variables are missing');
  }
} catch (err) {
  console.error('[Supabase] Failed to initialize Supabase client:', err);
  try {
    captureFrontendException(err instanceof Error ? err : new Error(String(err)), 'config', {
      flow_type: 'supabase_init_failure'
    });
  } catch {
    // Sentry guard
  }
  supabaseInstance = createDummyClient(err instanceof Error ? err.message : 'Supabase initialization failed');
}

export const supabase = supabaseInstance;

export const checkSupabaseConfig = () => {
  if (!supabaseUrl || !supabaseAnonKey) {
    return false;
  }
  return true;
};

export interface PaymentRequest {
  id: string;
  business_id: string;
  business_name: string;
  amount: number;
  method: 'mobile_money' | 'bank_transfer';
  status: 'pending' | 'paid' | 'rejected';
  reference: string;
  created_at: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  logo_url: string;
  primary_color: string;
  secondary_color: string;
  tagline: string;
  custom_domain?: string;
  status: 'active' | 'suspended';
  manager_id?: string;
  target_country?: string;
  created_at?: string;
}

export type TrialStatus = 
  | 'activation' 
  | 'activation_expired' 
  | 'founding' 
  | 'subscription_required' 
  | 'starter' 
  | 'growth' 
  | 'premium';

export interface Business {
  id: string;
  user_id: string;
  business_name?: string;
  name?: string; // Standardized display name
  slug?: string;
  country: string;
  tenant_id?: string;
  business_type: 'micro' | 'registered';
  verification_status: 'pending_review' | 'identity_verified' | 'business_verified' | 'approved' | 'rejected' | 'suspended';
  subscription_status?: 'trialing' | 'active' | 'suspended_trial_expired' | 'suspended_past_due' | 'cancelled';
  trial_ends_at?: string;
  activation_started_at?: string;
  activation_expires_at?: string;
  founding_started_at?: string;
  founding_expires_at?: string;
  trial_status?: TrialStatus;
  verification_docs?: any;
  email?: string;
  location?: string;
  whatsapp_number?: string;
  identity_number?: string;
  office_address?: string;
  description?: string;
  moderation_notes?: string;
  logo_url?: string;
  banner_url?: string;
  primary_color?: string;
  secondary_color?: string;
  tagline?: string;
  category?: string;
  subscription_plan?: 'starter' | 'growth' | 'premium';
  subscription_period?: 'monthly' | 'yearly';
  last_billing_date?: string;
  subscription_price_locked?: number;
  founder_pricing?: boolean;
  founder_since?: string;
  cancelled_at?: string;
  ai_usage_count?: number;
  badge_type?: 'verified_micro' | 'verified_registered';
  badge_awarded_at?: string;
  badge_awarded_by?: string;
  trust_score?: number;
  created_at?: string;
}

export interface BusinessLocation {
  id: string;
  business_id: string;
  name: string;
  city: string;
  address?: string;
  phone?: string;
  is_active: boolean;
  is_default: boolean;
  created_at?: string;
}

export interface Review {
  id: string;
  listing_id: string;
  business_id: string;
  customer_id: string;
  customer_whatsapp?: string; // Missing in previous def
  rating: number;
  comment: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  listings?: {
    title: string;
  };
}

export interface User {
  id: string;
  auth_user_id?: string;
  role: 'platform_admin' | 'admin' | 'business' | 'customer' | 'tenant_manager' | 'employee';
  full_name: string;
  email?: string;
  avatar_url?: string;
  whatsapp_number?: string;
  business_type?: 'none' | 'micro' | 'registered';
  verification_status?: 'pending_review' | 'identity_verified' | 'business_verified' | 'rejected' | 'suspended' | 'none';
  id_card_url?: string;
  registration_certificate_url?: string;
  is_suspended?: boolean;
  agreed_to_terms?: boolean;
  agreed_at?: string;
  privacy_version?: string;
}

export type ListingType = 'service' | 'product' | 'affiliate' | 'wash_service' | 'home_cleaning' | 'laundry' | 'car_sale' | 'spare_part' | 'tyres' | 'battery' | 'mechanic_service' | 'barber_service' | 'water_service' | 'solar_installation' | 'construction_service' | 'professional_service';

export interface PricingTier {
  category?: 'small' | 'medium' | 'large' | 'luxury';
  price: number;
}

export interface Listing {
  id: string;
  business_id?: string;
  title: string;
  type: ListingType;
  listing_type?: string | null;
  interaction_mode?: 'booking' | 'enquiry';
  price?: number;
  business_name?: string;
  business_type?: 'none' | 'micro' | 'registered';
  verification_status?: 'pending_review' | 'identity_verified' | 'business_verified' | 'rejected' | 'suspended';
  is_verified?: boolean;
  is_active: boolean;
  location: string;
  location_id?: string;
  category?: string;
  images?: string[];
  description: string;
  pricing_tiers?: PricingTier[] | Record<string, number>;
  currency?: string;
  is_official: boolean;
  whatsapp_number?: string;
  business_slug?: string;
  is_flagged?: boolean;
  flag_reason?: string;
  badge_type?: 'verified_micro' | 'verified_registered';
  trust_score?: number;
  country?: string;
  track_inventory?: boolean;
  stock_quantity?: number;
  businesses?: any;
  pricing_type?: 'fixed' | 'starts_at' | 'estimate_required';
  availability_status?: 'in_stock' | 'limited' | 'by_order' | 'unavailable';
  fulfillment_support?: 'pickup' | 'delivery' | 'courier' | 'nationwide';
  lead_time_hours?: number;
  delivery_supported?: boolean;
  capacity_state?: 'instant' | 'requires_booking' | 'backlog';
  is_affiliate?: boolean;
  affiliate_url?: string;
  affiliate_button_text?: string;
  created_at?: string;
}

export interface CarWashService {
  id: string;
  business_id: string;
  title: string;
  description: string;
  location: string;
  service_name: string;
  images: string[];
  pricing_tiers: {
    small: number;
    medium: number;
    large: number;
    luxury: number;
  };
  is_active: boolean;
  is_official: boolean;
  created_at?: string;
}

export interface Employee {
  id: string;
  business_id: string;
  name: string;
  employee_number: string;
  photo_url: string;
  phone_number?: string;
  email?: string;
  invite_token?: string;
  invite_status?: 'pending' | 'accepted' | 'expired';
  user_id?: string;
  role?: string;
  created_at?: string;
}

export interface Subscription {
  status: 'trialing' | 'active' | 'suspended_trial_expired' | 'suspended_past_due' | 'cancelled';
  trial_ends_at: string;
  plan: 'starter' | 'growth' | 'premium';
}

export interface Booking {
  id: string;
  listing_id: string;
  business_id: string;
  user_id: string;
  customer_name: string;
  customer_whatsapp: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  service_location: string;
  location_id?: string;
  preferred_datetime: string;
  listing_title?: string;
  service_name: string;
  service_category: string;
  calculated_price: number;
  price?: number; // For backward compatibility
  assigned_employee_id?: string;
  assigned_employee?: {
    name: string;
    photo_url: string;
  };
  created_at: string;
}

export interface Enquiry {
  id: string;
  listing_id: string;
  business_id: string;
  listing_title: string;
  user_id?: string;
  customer_name: string;
  customer_whatsapp: string;
  message: string;
  status: 'new' | 'pending' | 'contacted' | 'negotiating' | 'won' | 'lost' | 'responded' | 'closed' | 'ignored';
  sale_amount?: number;
  closed_at?: string;
  updated_at?: string;
  outcome_notes?: string;
  created_at: string;
  is_flagged?: boolean;
  ai_priority?: string;
  ai_sentiment?: string;
  assigned_employee_id?: string;
  assigned_employee?: {
    name: string;
    photo_url: string;
  };
  interaction_type?: 'enquiry' | 'booking' | string;
  interaction_details?: {
    preferred_datetime?: string;
    booking_time?: string;
    calculated_price?: number;
    category?: string;
    package_type?: string;
    location_id?: string;
    service_name?: string;
    service_location?: string;
    [key: string]: any;
  } | null;
}
